import { useState } from 'react';
import { Icon } from '@iconify/react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover';
import { cn } from '@/lib/utils';
import type { CalendarField } from '@/services/bookingCalendarService';

interface FieldPickerMenuProps {
  fields: CalendarField[];
  selectedSlug: string | null;
  onSelect: (slug: string | null) => void;
}

export default function FieldPickerMenu({ fields, selectedSlug, onSelect }: FieldPickerMenuProps) {
  const [open, setOpen] = useState(false);
  const effectiveSelectedSlug = fields.length === 1 ? fields[0].slug : selectedSlug;
  const currentField = fields.find((item) => item.slug === effectiveSelectedSlug);
  const buttonLabel = currentField ? currentField.name : 'Semua Lapangan';

  const choose = (slug: string | null) => {
    if (fields.length === 1) {
      onSelect(fields[0].slug);
      setOpen(false);
      return;
    }
    onSelect(slug);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex h-10 items-center gap-2 rounded-full border border-[#E0E6ED] bg-white px-4 text-sm font-semibold text-[#1F1F1F] shadow-sm transition-colors hover:bg-gray-50"
        >
          <Icon icon="ph:squares-four" className="h-4 w-4 text-gray-600" />
          <span>{buttonLabel}</span>
          <Icon icon="ph:caret-down" className="h-3.5 w-3.5 text-gray-500" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 rounded-2xl border border-[#E0E6ED] bg-white p-2 shadow-lg sm:w-80">
        <div className="space-y-1">
          {/* Option: Semua Lapangan (hanya ditampilkan jika ada lebih dari 1 lapangan aktif) */}
          {fields.length > 1 && (
            <button
              type="button"
              className={cn(
                'flex w-full items-center gap-3 rounded-xl p-3 text-left transition-colors',
                effectiveSelectedSlug === null ? 'bg-[#FFF5EE]' : 'hover:bg-gray-50'
              )}
              onClick={() => choose(null)}
            >
              <div
                className={cn(
                  'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                  effectiveSelectedSlug === null ? 'border-[#FC480D]' : 'border-gray-300'
                )}
              >
                {effectiveSelectedSlug === null && <span className="h-2 w-2 rounded-full bg-[#FC480D]" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className={cn('truncate text-sm font-semibold', effectiveSelectedSlug === null ? 'text-[#FC480D]' : 'text-[#0E1726]')}>
                  Semua Lapangan
                </p>
                <p className="text-xs text-gray-500">{fields.length} lapangan</p>
              </div>
            </button>
          )}

          {/* Individual Field Options */}
          {fields.map((field) => {
            const isSelected = effectiveSelectedSlug === field.slug;
            return (
              <button
                key={field.slug}
                type="button"
                className={cn(
                  'flex w-full items-center gap-3 rounded-xl p-3 text-left transition-colors',
                  isSelected ? 'bg-[#FFF5EE]' : 'hover:bg-gray-50'
                )}
                onClick={() => choose(field.slug)}
              >
                <div
                  className={cn(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                    isSelected ? 'border-[#FC480D]' : 'border-gray-300'
                  )}
                >
                  {isSelected && <span className="h-2 w-2 rounded-full bg-[#FC480D]" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className={cn('truncate text-sm font-semibold', isSelected ? 'text-[#FC480D]' : 'text-[#0E1726]')}>
                    {field.name}
                  </p>
                  <p className="truncate text-xs text-gray-500">{field.sport_name || 'Vinyl · Indoor'}</p>
                </div>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
